import { IChartProps, IStatisticsResponseModel } from "../models/chart";
import {
  ModelType,
  StatisticsDataInput,
  TimePeriod,
  eventType,
} from "../../../../../API";

import ApexOptions from "../../../../../common/components/apexchart/ApexChartModels";
import convertDatesHelper from "../helpers/convertDatesHelper";
import { createChartTooltip } from "../../system-health/common/chartTooltip";
import { useGetStatisticsData } from "./useGetStatisticsData";
import { useMemo } from "react";
import { useTheme } from "@mui/material";

export const usePPEInfractionsChart = (
  timePeriod: TimePeriod,
  locations: string[] | null
): IChartProps => {
  const theme = useTheme();

  const filterParams = useMemo(
    (): Pick<
      StatisticsDataInput,
      "eventType" | "models" | "timePeriod" | "locations"
    > => ({
      eventType: eventType.PPEInfractions,
      models: [ModelType.hardhat],
      locations,
      timePeriod,
    }),
    [timePeriod, theme.palette.mode]
  );

  const { data, loading, refetching, refetchChartData } =
    useGetStatisticsData<IStatisticsResponseModel[]>(filterParams);

  const convertDates = convertDatesHelper(data, timePeriod);
  const convertValues = data.map((element): number | null =>
    element.record_count ? +Number(element.record_count).toFixed(3) : null
  );

  const tooltip = useMemo(() => createChartTooltip(), []);

  const options: ApexOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        type: "line",
        stacked: false,
        width: "100%",
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        fontFamily: "Inter",
      },
      theme: {
        mode: theme.palette.mode,
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      markers:
        convertValues.length === 1
          ? {
              size: 4,
              colors: ["#F3B344"],
            }
          : {},
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
      colors: ["#F3B344"],
      yaxis: {
        opposite: true,
      },
      xaxis: {
        tooltip: { enabled: false },
        categories: convertDates,
      },
      tooltip,
    }),
    [data, theme.palette.mode]
  );

  const series: ApexAxisChartSeries = useMemo(
    (): ApexAxisChartSeries => [
      {
        name: "Total infractions",
        data: convertValues,
      },
    ],
    [convertValues]
  );

  return {
    series,
    options,
    loading,
    refetching,
    refetchChartData,
    showNoData:
      !loading && !refetching && data?.filter(d => d.record_count).length === 0,
  };
};

export default usePPEInfractionsChart;
